/**
 * Enhanced Route Configuration
 *
 * This file provides a type-safe, traceable route configuration
 * that automatically maps routes to components using the registry system.
 */

import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ROUTE_MAPPINGS } from './route-mapping';
import { getComponent } from './component-registry';
import { AppShell } from '../components/AppShell';
import { useUser } from '../contexts/UserContext';
import { isPlatformAdministrator } from '../components/AppShell/appNavAccess';
import { canManageSelectedCompany } from '../components/AppShell/companyAdminAccess';
import { CONTROL_BRIDGE_HOME_PATH } from '../utils/controlBridgeRoutes';

// ============================================================================
// ROUTE COMPONENT WRAPPER
// ============================================================================

interface RouteWrapperProps {
  componentName: string;
  props?: Record<string, any>;
}

const RouteWrapper: React.FC<RouteWrapperProps> = ({ componentName, props = {} }) => {
  const Component = getComponent(componentName);

  if (!Component) {
    console.error(`Component ${componentName} not found in registry`);
    return <div>Component not found: {componentName}</div>;
  }

  return <Component {...props} />;
};

/** Auth gate with login redirect preserving `next` (golden path: paywall, confirmation, etc.). */
const ProtectedRouteElement: React.FC<{
  requiresAuth: boolean;
  requiresPlatformAdmin?: boolean;
  requiresCompanyAdmin?: boolean;
  componentName: string;
  routeProps?: Record<string, unknown>;
}> = ({
  requiresAuth,
  requiresPlatformAdmin = false,
  requiresCompanyAdmin = false,
  componentName,
  routeProps = {},
}) => {
  const { isAuthenticated, user, selectedCompany, availableCompanies } = useUser();
  const location = useLocation();

  if (requiresAuth && !isAuthenticated) {
    const next = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  if (requiresPlatformAdmin && !isPlatformAdministrator(user)) {
    return <Navigate to={CONTROL_BRIDGE_HOME_PATH} replace />;
  }

  if (requiresCompanyAdmin) {
    const membership = availableCompanies.find((row) => row.company.id === selectedCompany?.id);
    if (!canManageSelectedCompany(user, membership?.instance.role)) {
      return <Navigate to={CONTROL_BRIDGE_HOME_PATH} replace />;
    }
  }

  return (
    <RouteWrapper
      componentName={componentName}
      props={
        componentName === 'ArtifactTransitionTracer'
          ? { artifactId: '', artifactName: '' }
          : routeProps
      }
    />
  );
};

// ============================================================================
// ENHANCED ROUTES COMPONENT
// ============================================================================

export const EnhancedAppRoutes: React.FC = () => {
  // Only the initial session bootstrap uses UserContext.isLoading. Login/MFA must not
  // flip that flag — unmounting <Routes> mid-signup wipes RegisterPage MFA state.
  const { isLoading: isSessionBootstrapping } = useUser();

  if (isSessionBootstrapping) {
    return (
      <AppShell>
        <div style={{ padding: '24px', textAlign: 'center' }}>Checking your session...</div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <Routes>
        {Object.entries(ROUTE_MAPPINGS)
          .filter(([, route]) => !(route.hideInProduction && process.env.NODE_ENV === 'production'))
          .map(([key, route]) => (
          <Route
            key={key}
            path={route.path}
            element={
              <ProtectedRouteElement
                requiresAuth={
                  route.requiresAuth !== undefined ? route.requiresAuth : route.domain !== 'shared'
                }
                requiresPlatformAdmin={Boolean(route.requiresPlatformAdmin)}
                requiresCompanyAdmin={Boolean(route.requiresCompanyAdmin)}
                componentName={route.component}
                routeProps={
                  route.component === 'ArtifactTransitionTracer'
                    ? { artifactId: '', artifactName: '' }
                    : undefined
                }
              />
            }
          />
        ))}
        {/* Catch-all route to redirect unknown paths to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
};

// ============================================================================
// ROUTE GENERATION UTILITIES
// ============================================================================

/**
 * Generate route configuration for a specific domain
 */
export const generateDomainRoutes = (domain: 'company' | 'prime' | 'shared' | 'admin') => {
  return Object.entries(ROUTE_MAPPINGS)
    .filter(([_, route]) => route.domain === domain)
    .map(([key, route]) => ({
      key,
      ...route,
    }));
};

/**
 * Generate breadcrumb configuration
 */
export const generateBreadcrumbConfig = () => {
  return Object.entries(ROUTE_MAPPINGS).reduce(
    (acc, [_, route]) => {
      if (route.breadcrumb) {
        acc[route.path] = {
          label: route.breadcrumb,
          domain: route.domain,
          parent: route.parent,
        };
      }
      return acc;
    },
    {} as Record<string, { label: string; domain: string; parent?: string }>
  );
};

/**
 * Get route metadata for debugging
 */
export const getRouteMetadata = () => {
  return {
    totalRoutes: Object.keys(ROUTE_MAPPINGS).length,
    companyRoutes: Object.values(ROUTE_MAPPINGS).filter((r) => r.domain === 'company').length,
    primeRoutes: Object.values(ROUTE_MAPPINGS).filter((r) => r.domain === 'prime').length,
    sharedRoutes: Object.values(ROUTE_MAPPINGS).filter((r) => r.domain === 'shared').length,
    adminRoutes: Object.values(ROUTE_MAPPINGS).filter((r) => r.domain === 'admin').length,
    routes: ROUTE_MAPPINGS,
  };
};
